/**
 * Top Bar Component
 * Provides top navigation bar with user info and controls
 * Enhanced with enterprise-grade styling and hover dropdown menus
 */

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Breadcrumbs,
  Link,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  Grow,
  ClickAwayListener,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout,
  NotificationsNone as NotificationsIcon,
  Dashboard as DashboardIcon,
  Devices as DevicesIcon,
  Map as MapIcon,
  Analytics as AnalyticsIcon,
  QueryBuilder as QueryIcon,
  CloudDownload as InfluxIcon,
  Storage as DynamoIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ArrowDropDown,
  Warning as WarningIcon,
  Assignment as TicketIcon,
  Rule as RuleIcon,
} from '@mui/icons-material';
import { useStore } from '../store/store';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../shared/constants/roles';
import { ROUTES } from '../shared/constants/routes';

// Page title mapping
const PAGE_TITLES = {
  [ROUTES.DASHBOARD]: 'Dashboard',
  [ROUTES.DEVICES]: 'Devices',
  [ROUTES.MAP]: 'Map View',
  [ROUTES.ANALYTICS]: 'Analytics',
  [ROUTES.QUERIES]: 'Queries',
  [ROUTES.INFLUXDB]: 'InfluxDB',
  [ROUTES.DYNAMODB]: 'DynamoDB',
  [ROUTES.USERS]: 'Users',
  [ROUTES.SETTINGS]: 'Settings',
};

// Navigation menu structure with dropdown items
// Operations-focused: Real-time monitoring, alerts, faults, and configurations
const NAVIGATION_MENUS = [
  {
    label: 'Monitoring',
    icon: <DashboardIcon />,
    items: [
      { label: 'Dashboard', route: ROUTES.DASHBOARD, icon: <DashboardIcon />, description: 'Real-time system overview and metrics' },
      { label: 'Map View', route: ROUTES.MAP, icon: <MapIcon />, description: 'Geographic view of all devices' },
      { label: 'Device Management', route: ROUTES.DEVICES, icon: <DevicesIcon />, description: 'Monitor and manage IoT devices' },
    ],
  },
  {
    label: 'Alerts & Faults',
    icon: <WarningIcon />,
    items: [
      { label: 'Active Alerts', route: ROUTES.ALERTS || '/alerts', icon: <NotificationsIcon />, description: 'View and manage active alerts' },
      { label: 'Fault Conditions', route: ROUTES.FAULTS || '/faults', icon: <WarningIcon />, description: 'Monitor fault conditions and rules' },
      { label: 'Alert Rules', route: ROUTES.ALERT_RULES || '/alert-rules', icon: <RuleIcon />, description: 'Configure alert rules and thresholds' },
    ],
  },
  {
    label: 'Operations',
    icon: <QueryIcon />,
    items: [
      { label: 'Real-Time Data', route: ROUTES.INFLUXDB, icon: <InfluxIcon />, description: 'Live time-series data queries' },
      { label: 'Device Data', route: ROUTES.DYNAMODB, icon: <DynamoIcon />, description: 'Current device status and records' },
      { label: 'Query Builder', route: ROUTES.QUERIES, icon: <QueryIcon />, description: 'Build custom data queries' },
    ],
  },
  {
    label: 'Ticketing',
    icon: <TicketIcon />,
    items: [
      { label: 'Open Tickets', route: ROUTES.TICKETS || '/tickets', icon: <TicketIcon />, description: 'View and manage open tickets' },
      { label: 'Ticket History', route: ROUTES.TICKET_HISTORY || '/tickets/history', icon: <AnalyticsIcon />, description: 'Historical ticket records' },
      { label: 'Create Ticket', route: ROUTES.CREATE_TICKET || '/tickets/create', icon: <TicketIcon />, description: 'Create new support ticket' },
    ],
  },
  {
    label: 'Configuration',
    icon: <SettingsIcon />,
    items: [
      { label: 'User Management', route: ROUTES.USERS, icon: <PeopleIcon />, description: 'Manage users and permissions' },
      { label: 'System Settings', route: ROUTES.SETTINGS, icon: <SettingsIcon />, description: 'Configure system parameters' },
      { label: 'Analytics', route: ROUTES.ANALYTICS, icon: <AnalyticsIcon />, description: 'Data insights and trends' },
    ],
  },
];

// Breadcrumbs removed - using simplified page title only

function TopBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const toggleSidebar = useStore((state) => state.toggleSidebar);
  const { user, logout, userRole } = useAuth();
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState({});
  
  const currentPageTitle = PAGE_TITLES[location.pathname] || 'Dashboard';

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const userEmail = user?.signInDetails?.loginId || user?.username || 'User';
  const userInitial = userEmail.charAt(0).toUpperCase();

  const getRoleLabel = (role) => {
    const labels = {
      [ROLES.SUPER_ADMIN]: 'Super Admin',
      [ROLES.ADMIN]: 'Admin',
      [ROLES.MANAGER]: 'Manager',
      [ROLES.OPERATOR]: 'Operator',
      [ROLES.VIEWER]: 'Viewer',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      [ROLES.SUPER_ADMIN]: 'error',
      [ROLES.ADMIN]: 'warning',
      [ROLES.MANAGER]: 'info',
      [ROLES.OPERATOR]: 'success',
      [ROLES.VIEWER]: 'default',
    };
    return colors[role] || 'default';
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0px 1px 0px rgba(0,0,0,0.05)',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', px: 3 }}>
        <IconButton
          edge="start"
          onClick={toggleSidebar}
          sx={{ 
            mr: 2,
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Navigation Tabs with Hover Dropdowns */}
        <Box sx={{ display: 'flex', gap: 0.5, mr: 4 }}>
          {NAVIGATION_MENUS.map((menu) => {
            const isActive = menu.items.some(item => location.pathname === item.route);
            
            return (
              <Box
                key={menu.label}
                onMouseEnter={(e) => {
                  setHoveredMenu(menu.label);
                  setMenuAnchorEl(prev => ({ ...prev, [menu.label]: e.currentTarget }));
                }}
                onMouseLeave={() => {
                  setTimeout(() => {
                    setHoveredMenu(null);
                  }, 200);
                }}
                sx={{
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    color: isActive ? 'primary.main' : 'text.secondary',
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.9375rem',
                    borderRadius: '8px 8px 0 0',
                    borderBottom: isActive ? '2px solid' : '2px solid transparent',
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {React.cloneElement(menu.icon, { sx: { fontSize: 18 } })}
                  <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                    {menu.label}
                  </Typography>
                  <ArrowDropDown sx={{ fontSize: 18, ml: -0.5 }} />
                </Box>

                {/* Dropdown Menu */}
                {hoveredMenu === menu.label && (
                  <Popper
                    open={true}
                    anchorEl={menuAnchorEl[menu.label]}
                    placement="bottom-start"
                    transition
                    sx={{
                      zIndex: 1300,
                      mt: 0.5,
                    }}
                    onMouseEnter={() => setHoveredMenu(menu.label)}
                    onMouseLeave={() => setHoveredMenu(null)}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps}>
                        <Paper
                          elevation={8}
                          sx={{
                            minWidth: 280,
                            mt: 0.5,
                            borderRadius: 2,
                            overflow: 'hidden',
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <List dense sx={{ py: 0.5 }}>
                            {menu.items.map((item) => {
                              const isItemActive = location.pathname === item.route;
                              return (
                                <ListItem key={item.route} disablePadding>
                                  <ListItemButton
                                    onClick={() => {
                                      navigate(item.route);
                                      setHoveredMenu(null);
                                    }}
                                    sx={{
                                      py: 1,
                                      px: 2,
                                      backgroundColor: isItemActive ? 'action.selected' : 'transparent',
                                      '&:hover': {
                                        backgroundColor: 'action.hover',
                                      },
                                    }}
                                  >
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      {React.cloneElement(item.icon, {
                                        sx: {
                                          fontSize: 20,
                                          color: isItemActive ? 'primary.main' : 'text.secondary',
                                        },
                                      })}
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={item.label}
                                      primaryTypographyProps={{
                                        fontSize: '0.875rem',
                                        fontWeight: isItemActive ? 600 : 400,
                                        color: isItemActive ? 'primary.main' : 'text.primary',
                                      }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </List>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Page Title - Simplified, no breadcrumbs */}
        <Box sx={{ flexGrow: 1 }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 600, 
              color: 'text.primary', 
              fontSize: '1.125rem', 
              letterSpacing: '-0.01em',
              lineHeight: 1.2,
            }}
          >
            {currentPageTitle}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Notifications */}
          <IconButton 
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'text.primary',
              },
            }}
          >
            <NotificationsIcon />
          </IconButton>

          {/* Role Badge */}
          {userRole && (
            <Chip
              label={getRoleLabel(userRole)}
              color={getRoleColor(userRole)}
              size="small"
              sx={{ height: 24, fontWeight: 500 }}
            />
          )}

          {/* User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                {userEmail}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                p: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'secondary.main',
                  fontWeight: 600,
                }}
              >
                {userInitial}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  boxShadow: '0px 4px 16px rgba(0,0,0,0.15)',
                },
              }}
            >
              <MenuItem onClick={handleMenuClose} disabled>
                <AccountCircle sx={{ mr: 1.5 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {userEmail}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getRoleLabel(userRole)}
                  </Typography>
                </Box>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1.5 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
